import { useMemo } from "react";
import I18n from "@/i18n-js/instance";
import { useBannerVisibility } from "@/react/components/Layout/TrialBanner/useBannerVisibility";
import { distanceBetweenDates } from "@/react/helpers/dateTimeHelpers";
import { dateStringToTimeAgo } from "@/react/helpers/dateTimeHelpers/timeAgo";
import { useEventsTracker } from "@/react/hooks/analytics/useEventsTracker";
import { useCountdownTimer } from "@/react/hooks/useCountdownTimer";
import { usePlanStatus } from "@/react/hooks/usePlanStatus";
import { formatDateString } from "@circle-react/helpers/dateTimeHelpers/getLocalizedDate";

export const useTrialBanner = () => {
  const { currentCommunity, shouldHideBanner } = useBannerVisibility();
  const { isCommunityOnTrial } = usePlanStatus();
  const { trackEvent } = useEventsTracker();

  const expiringInTimer = useCountdownTimer({
    endDate: currentCommunity?.trial_ends_at || undefined,
  });

  const trialEndDateFormatted = useMemo(() => {
    if (!currentCommunity?.trial_ends_at) return "";
    return formatDateString(currentCommunity.trial_ends_at, {
      day: "2-digit",
      month: "2-digit",
    });
  }, [currentCommunity]);

  const trialEndDateMonthAndDayFormatted = useMemo(() => {
    if (!currentCommunity?.trial_ends_at) return "";
    return formatDateString(currentCommunity.trial_ends_at, {
      month: "long",
      day: "2-digit",
    });
  }, [currentCommunity]);

  const trialEndsInWords = useMemo(
    () =>
      currentCommunity?.trial_ends_at
        ? dateStringToTimeAgo(currentCommunity.trial_ends_at)
        : "",
    [currentCommunity],
  );

  const daysLeft = distanceBetweenDates(
    new Date(),
    new Date(currentCommunity?.trial_ends_at || ""),
  );

  const trialDaysRemainingInWords = useMemo(
    () =>
      // Including less than 1 to avoid "0 days left"
      daysLeft <= 1
        ? `1 ${I18n.t("trial_banner.day")}`
        : `${daysLeft} ${I18n.t("trial_banner.days")}`,
    [daysLeft],
  );

  const isSubscriptionCancelled = useMemo(() => {
    if (isCommunityOnTrial) {
      return false;
    }

    return currentCommunity?.subscription
      ? Boolean(currentCommunity.subscription.is_cancelled)
      : false;
  }, [currentCommunity, isCommunityOnTrial]);

  const trackTrialBannerClick = () => {
    trackEvent("trial_banner.upgrade_now.clicked", {
      days_left: daysLeft,
    });
  };

  const shouldShowTrialBanner = useMemo(
    () =>
      Boolean(
        !shouldHideBanner && (isCommunityOnTrial || isSubscriptionCancelled),
      ),
    [shouldHideBanner, isCommunityOnTrial, isSubscriptionCancelled],
  );

  const trialBannerElement = document.getElementById("trial-banner");
  const isTrialBannerExists = Boolean(trialBannerElement);

  return {
    trialEndsInWords,
    trialDaysRemainingInWords,
    trialEndDateFormatted,
    trialEndDateMonthAndDayFormatted,
    daysLeft,
    expiringInTimer,
    isSubscriptionCancelled,
    isCommunityOnTrial,
    shouldShowTrialBanner,
    isTrialBannerExists,
    trackTrialBannerClick,
  };
};
